import { useState, useMemo, useEffect } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Tooltip,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Loader,
  Center,
  Select,
  Pill,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconCheck,
  IconX,
  IconFilter,
  IconRefresh,
} from "@tabler/icons-react";
import {
  useFacilitiesAdmin,
  useCreateFacility,
  useUpdateFacility,
  useToggleFacilityActive,
} from "../../../hooks/admin/useFacilities";
import FacilityForm, {
  FacilityFormValues,
} from "../../../components/admin/facilities/FacilityForm";
import {
  Facility,
  facilitiesService,
} from "../../../services/admin/facilities.service";

/** Fetches a presigned download URL for a logo S3 key and renders a thumbnail. */
function FacilityLogoThumbnail({ s3Key }: { s3Key: string }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    facilitiesService
      .getLogoDownloadUrl(s3Key)
      .then(setUrl)
      .catch(() => {});
  }, [s3Key]);

  if (!url) return <Loader size={14} />;
  return (
    <img
      src={url}
      alt="Facility logo"
      style={{
        maxHeight: 32,
        maxWidth: 72,
        objectFit: "contain",
        display: "block",
      }}
    />
  );
}

const ITEMS_PER_PAGE = 20;

interface FacilityFilters {
  status?: "all" | "active" | "inactive";
}

export default function FacilitiesAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null);
  const [facilityToToggle, setFacilityToToggle] = useState<Facility | null>(
    null
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<FacilityFilters>({});
  const [tempFilters, setTempFilters] = useState<FacilityFilters>({});
  const [page, setPage] = useState(1);

  // Data hooks
  const { data: facilities = [], isLoading, refetch } = useFacilitiesAdmin();
  const createFacility = useCreateFacility();
  const updateFacility = useUpdateFacility();
  const toggleFacilityActive = useToggleFacilityActive();

  // Filter facilities
  const filteredFacilities = useMemo(() => {
    let result = facilities;

    // Filter by status
    if (filters.status === "active") {
      result = result.filter((f) => f.is_active);
    } else if (filters.status === "inactive") {
      result = result.filter((f) => !f.is_active);
    }

    // Filter by search text
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (f) =>
          f.name?.toLowerCase().includes(searchLower) ||
          f.address?.toLowerCase().includes(searchLower)
      );
    }

    return result;
  }, [facilities, debouncedSearch, filters]);

  // Check if any filters are active
  const hasActiveFilters = filters.status && filters.status !== "all";

  // Pagination
  const totalPages = Math.ceil(filteredFacilities.length / ITEMS_PER_PAGE);
  const paginatedFacilities = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredFacilities.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredFacilities, page]);

  // Handlers
  const handleAdd = () => {
    setEditingFacility(null);
    openFormModal();
  };

  const handleEdit = (facility: Facility) => {
    setEditingFacility(facility);
    openFormModal();
  };

  const handleSubmit = async (values: FacilityFormValues) => {
    if (editingFacility) {
      await updateFacility.mutateAsync({
        id: editingFacility.id,
        name: values.name,
        address: values.address,
        phone: values.phone || undefined,
        fax: values.fax || undefined,
        is_active: values.is_active,
        logoS3Key: values.logoS3Key || undefined,
      });
    } else {
      await createFacility.mutateAsync({
        name: values.name,
        address: values.address,
        phone: values.phone || undefined,
        fax: values.fax || undefined,
        is_active: values.is_active,
      });
    }
    closeFormModal();
  };

  const handleToggleActive = (facility: Facility) => {
    setFacilityToToggle(facility);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (facilityToToggle) {
      await toggleFacilityActive.mutateAsync(facilityToToggle.id);
      closeConfirmModal();
      setFacilityToToggle(null);
    }
  };

  const handleOpenFilterModal = () => {
    setTempFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...tempFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPage(1);
  };

  const handleRemoveFilter = (key: keyof FacilityFilters) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
    setPage(1);
  };

  // Get display names for active filters
  const getFilterDisplayName = (key: keyof FacilityFilters): string | null => {
    if (key === "status" && filters.status && filters.status !== "all") {
      return filters.status === "active" ? "Active Only" : "Inactive Only";
    }
    return null;
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Facilities</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage laboratory facilities and locations
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name or address..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" align="center">
                    <Text size="sm" c="dimmed">
                      Filters:
                    </Text>
                    {filters.status && filters.status !== "all" && (
                      <Pill
                        withRemoveButton
                        onRemove={() => handleRemoveFilter("status")}
                      >
                        {getFilterDisplayName("status")}
                      </Pill>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  Showing {filteredFacilities.length} of {facilities.length}{" "}
                  facilities
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Facilities ({filteredFacilities.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(
                        page * ITEMS_PER_PAGE,
                        filteredFacilities.length
                      )}{" "}
                      of {filteredFacilities.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredFacilities.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {search || hasActiveFilters
                    ? "No facilities match the current filters"
                    : "No facilities found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Address</Table.Th>
                      <Table.Th>Phone</Table.Th>
                      <Table.Th>Fax</Table.Th>
                      <Table.Th>Logo</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Created</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedFacilities.map((facility) => (
                      <Table.Tr
                        key={facility.id}
                        style={{
                          opacity: facility.is_active ? 1 : 0.5,
                        }}
                      >
                        {/* Name */}
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {facility.name}
                          </Text>
                        </Table.Td>

                        {/* Address */}
                        <Table.Td>
                          <Text size="sm" lineClamp={2}>
                            {facility.address}
                          </Text>
                        </Table.Td>

                        {/* Phone */}
                        <Table.Td>
                          <Text size="sm">
                            {facility.phone || (
                              <Text size="sm" c="dimmed">
                                —
                              </Text>
                            )}
                          </Text>
                        </Table.Td>

                        {/* Fax */}
                        <Table.Td>
                          <Text size="sm">
                            {facility.fax || (
                              <Text size="sm" c="dimmed">
                                —
                              </Text>
                            )}
                          </Text>
                        </Table.Td>

                        {/* Logo */}
                        <Table.Td>
                          {facility.logo_s3_key || facility.logoS3Key ? (
                            <FacilityLogoThumbnail
                              s3Key={
                                (facility.logo_s3_key || facility.logoS3Key)!
                              }
                            />
                          ) : (
                            <Text size="xs" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>

                        {/* Status */}
                        <Table.Td>
                          <Badge
                            color={facility.is_active ? "green" : "red"}
                            variant="light"
                            size="sm"
                          >
                            {facility.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>

                        {/* Created */}
                        <Table.Td>
                          <Text size="sm">
                            {new Date(facility.created_at).toLocaleDateString()}
                          </Text>
                        </Table.Td>

                        {/* Actions */}
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(facility)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={
                                facility.is_active ? "Deactivate" : "Activate"
                              }
                            >
                              <ActionIcon
                                variant="subtle"
                                color={facility.is_active ? "red" : "green"}
                                onClick={() => handleToggleActive(facility)}
                              >
                                {facility.is_active ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add/Edit Facility Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={editingFacility ? "Edit Facility" : "Add New Facility"}
          size="lg"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <FacilityForm
              initialValues={editingFacility || undefined}
              onSubmit={handleSubmit}
              isLoading={createFacility.isPending || updateFacility.isPending}
            />

            <Group justify="space-between" mt="md">
              <Button variant="subtle" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createFacility.isPending || updateFacility.isPending}
                onClick={() => {
                  const form = document.querySelector("form");
                  if (form) form.requestSubmit();
                }}
              >
                {editingFacility ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Facilities"
          size="sm"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Select
              label="Status"
              placeholder="All statuses"
              data={[
                { value: "all", label: "All" },
                { value: "active", label: "Active Only" },
                { value: "inactive", label: "Inactive Only" },
              ]}
              value={tempFilters.status || "all"}
              onChange={(value) =>
                setTempFilters((prev) => ({
                  ...prev,
                  status: value as FacilityFilters["status"],
                }))
              }
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFilterModal}>
                Cancel
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Confirmation Modal for Activate/Deactivate */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title={
            facilityToToggle?.is_active
              ? "Deactivate Facility"
              : "Activate Facility"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {facilityToToggle?.is_active ? "deactivate" : "activate"} the
              facility <strong>{facilityToToggle?.name}</strong>?
            </Text>
            {facilityToToggle?.is_active && (
              <Text size="sm" c="dimmed">
                Inactive facilities will not appear in dropdown selections.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={facilityToToggle?.is_active ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={toggleFacilityActive.isPending}
              >
                {facilityToToggle?.is_active ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
